/**
 * DropHubSurface — a Drop rendered in HUB style.
 *
 * This was 432 lines inside `DropPageMain`, which is how that file reached
 * 1,184 against the 400-line ceiling its own header cites. It is a
 * self-contained tree with its own data needs, so it is its own component and
 * `DropPageMain` simply chooses between the two styles.
 *
 * ## What lives here and what does not
 *
 * This is a JOIN: it knows a Drop and it knows the spine, and its whole job is
 * matching one to the other. Everything it renders is a spine component that
 * takes plain data — `SurfaceRosterRail` takes members, `SurfaceNewsRail`
 * takes items, `SurfaceStrip` takes cards — so none of them know what a Drop
 * is, and an esports or agency surface can mount the same components with its
 * own data.
 *
 * The shaping of Drop payloads into those props lives in `dropSpineData.ts`
 * alongside the rest of the join, not inline here, so this file stays a
 * composition rather than a transformation.
 *
 * ## The stage is the viewer
 *
 * Selecting a roster member or the trailer puts it ON the stage rather than
 * navigating away — that container exists to be watched in. Live state changes
 * the OCCUPANT, never the destination: live mounts a real player, offline takes
 * the member in our own chrome, because a channel embed for someone who is not
 * streaming fills the stage with the platform's "is offline" card.
 */

import React from "react";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import SEO from "@/components/SEO";
import DevourTopNav from "@/components/DevourTopNav";
import {ThemeProvider, type TenantId} from "@/widgets/ThemeProvider";
import {useGetDropRoster} from "@/hooks/drop/useGetDropRoster";
import {useGetDropGameNews} from "@/hooks/drop/useGetDropGameNews";
import useOnLogout from "@/hooks/useOnLogout";
import {toggleChatOpen} from "@/redux/meta/metaActions";

import {SurfaceStructure} from "@/surfaces/SurfaceStructure";
import {dropSlots} from "@/surfaces/dropSlots";
import {DistributionStyle} from "@devour/client";
import {DropHubTopBar} from "./sections/DropHubTopBar";
import {useDropHubData} from "./sections/useDropHubData";
import {buildSectionCtas, buildBarActions, scrollToStage} from "./sections/dropHubChrome";
import {makeRenderDropHubSlot} from "./sections/renderDropHubSlot";
import {withSectionTracking} from "./sections/withSectionTracking";
import SurfaceWheel from "@/surfaces/spine/SurfaceWheel";
import {useDropHubStage} from "./useDropHubStage";
import {SurfaceActionBar} from "@/surfaces/spine/SurfaceActionBar";
import {useSlotScrollSpy} from "@/surfaces/spine/useSlotScrollSpy";
import {SlotFootprint} from "@/surfaces/slots";
import {SURFACE_LEGAL_LINKS} from "@/surfaces/spine/legalLinks";
import {brandStyle} from "@/surfaces/spine/brandStyle";
import {povPreset} from "@/surfaces/povPresets";
import {spineTokenStyle} from "@/surfaces/spine/spineTokenStyle";
import {youTubeThumb} from "@/surfaces/spine/youTubeThumb";
import {povCopy} from "@/surfaces/spine/povCopy";
import {rosterMembers, isOffTitle, type SpineDrop} from "@/surfaces/spine/dropSpineData";
import {highlightForMember} from "@/widgets/rosterMember";
import {memberStageOccupant} from "./sections/memberStageOccupant";
import {useHubBrandStage} from "./sections/useHubBrandStage";

import type {DropSection} from "@/hooks/drop/useGetDrop";

export interface DropHubSurfaceProps {
    drop: any;
    /**
     * The resolved style for the channel this surface is being served on.
     * Passed in rather than resolved here because the CHANNEL decides it, and
     * the page already asked that question to choose between the two trees.
     */
    style: DistributionStyle;
    tenantId?: TenantId;
    isPublicHub: boolean;
    enabledSections: DropSection[];
    activeSection: string | null;
    setActiveSection: (id: string | null) => void;
    /** The page's own renderer, for section types the spine has no rail for. */
    renderSectionContent: (section: DropSection) => React.ReactNode;
    /** Opens the platform login modal — the bar's signed-out affordance. */
    handleLoginRequired?: () => void;
    user?: {
        nickname?: string; firstName?: string; lastName?: string; gravatar?: string;
        profilePicture?: { cdnUrl?: string };
    };
    pointDisplay: { pointsName: string; currentPoints: number; currentLevel: number };
    handleInviteClick: () => void;
    dispatch: (action: any) => any;
}

export const DropHubSurface: React.FC<DropHubSurfaceProps> = ({
    drop, style, tenantId, isPublicHub, enabledSections, activeSection, setActiveSection,
    renderSectionContent, user, pointDisplay, handleInviteClick, handleLoginRequired, dispatch,
}) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: roster} = useGetDropRoster(drop?.slug);
    const {data: gameNews} = useGetDropGameNews(drop?.slug);
    const {devourLogout} = useOnLogout();

    const filterGameNames = roster?.filterGameNames ?? [];
    /*
     * The roster's LiveFilterMode. Read here and threaded down rather than
     * re-derived per widget — HUB ignoring it entirely is the bug this fixes,
     * and two widgets deciding it independently would be the next one.
     */
    const filterMode = roster?.filterMode;
    const members = roster?.members ?? [];
    const highlights = roster?.highlights ?? [];
    const copy = povCopy(drop?.pov, drop?.dropType);

    /*
     * WHO GETS FEATURED — the roster's own filter decides, not this page. A
     * surface featuring someone live on a DIFFERENT title presents them as
     * repping this one, which is exactly what that filter exists to prevent.
     * Order: live and on-title, then on-title WITH a clip to watch, then
     * on-title, then whoever is there.
     *
     * The middle step is new: a title's roster with nobody live right now but
     * a recent clip of one of them is not the same as a roster with nothing to
     * watch at all, and featuring whoever comes first in the array over
     * whoever actually has something playable buries the one member a viewer
     * can do anything with.
     */
    const onTitle = members.filter(m => !isOffTitle(m, filterGameNames));
    /*
     * THE LAST FALLBACK NEEDS A STREAMING IDENTITY.
     *
     * "Whoever is first in the array" is a reasonable stand-in for a roster of
     * channels — somebody is always worth showing. It is wrong for a roster of
     * PEOPLE: a member with no channel is never live and never has a clip, so
     * featuring them promises something to watch that does not exist, in a
     * card headed "Sponsored creator", over artwork that already carries their
     * name. A roster with nobody to watch should feature nobody, leave the
     * column unreserved, and appear only in its own rail.
     */
    const watchable = (m: typeof members[number]): boolean =>
        Boolean((m as { platform?: string }).platform || (m as { dropId?: string }).dropId);
    const featured = onTitle.find(m => m.isLive) ??
        onTitle.find(m => highlightForMember(m, highlights)) ??
        onTitle.find(watchable) ?? members.find(watchable);

    /*
     * The Stage in two halves: `useHubBrandStage` builds what it shows when
     * idle (and why that is conditional), `useDropHubStage` owns the state and
     * the resolver call. Both lifted out for the 400-line ceiling.
     */
    const {base: stageBase, idleOccupant: stageIdleOccupant} =
        useHubBrandStage(drop as SpineDrop, members.length);

    /*
     * Identity-stage extras (BRAND anchor, rttf-drop-hub.html): the avatar
     * pile from the roster and CTAs pointing at the surface's own sections.
     * Section ids follow railItems' scheme so a CTA lands on the same band
     * the rail would.
     */
    const rosterCards = rosterMembers(members, filterGameNames, filterMode, roster?.rosterFields ?? []);
    const stageFaces = rosterCards.slice(0, 4).map(card => ({
        id: card.id,
        initials: card.name.split(/\s+/).map(word => word[0] ?? "")
            .join("")
            .slice(0, 2)
            .toUpperCase(),
        imageUrl: card.imageUrl,
    }));
    const railSections = (drop?.sections ?? []).filter(s => s.enabled !== false);
    const sectionNavId = (s: DropSection, i: number): string => s._id ?? `section-${i}`;
    const spinIdx = railSections.findIndex(s => s.engagementType === "GAMIFICATION");
    const boardIdx = railSections.findIndex(s => s.type === "LEADERBOARDS" || s.engagementType === "LEDGER_BOARD");

    const stage = useDropHubStage({idle: stageIdleOccupant, idleId: drop?.slug ?? "hub-idle"});
    const stageOccupant = stage.occupant;
    /*
     * Every path that fills the stage also brings it into view — the stage
     * swaps in place, so without this a tap looks like it did nothing.
     */
    const setStageOccupant = React.useCallback((occupant: Parameters<typeof stage.setOccupant>[0]) => {
        stage.setOccupant(occupant);
        if (occupant) scrollToStage();
    }, [stage]);
    const resolvedStage = stage.resolved;
    const structureStage = stage.structureStage;

    /*
     * Contested-column pin — the occupancy engine's user override (contract
     * §3). Wired here because SurfaceStructure forwards both props and its
     * only consumer never passed them, leaving the whole engine built and
     * undriven (Phase H gap register, defect 2). The control itself only
     * renders when the engine reports >1 candidate.
     */
    const [pinnedSlot, setPinnedSlot] = React.useState<string | null>(null);

    /* The hub's derived data — indexes, wheel access, standing. See the hook. */
    const {
        sectionById, contentBlocks, contentBlockById, liveBlockSlots,
        sectionSublabels, surfaceWheel, wheelOpenRequest, requestWheel,
        isWheelSection, wheelAccessible, myBadges, memberStanding, barStanding,
        campaignWeekLabel, memberName,
    } = useDropHubData({drop, enabledSections, railSections, fullToken, user});

    const sectionCtas = buildSectionCtas({
        railSections, sectionNavId, isWheelSection, wheelAccessible,
        requestWheel, setActiveSection, spinIdx, boardIdx,
    });

    /*
     * The bar reads the page, not the last press. Same pairing the bar entries
     * use — sections address their slot directly, a block entry is `band-<id>`
     * against a slot of `<id>`.
     */
    useSlotScrollSpy(
        [
            ...railSections
                .filter(s => !isWheelSection(s))
                .map((s, i) => ({navId: sectionNavId(s, i), slotId: sectionNavId(s, i)})),
            ...liveBlockSlots
                .filter(s => s.footprint !== SlotFootprint.STAGE)
                .map(s => ({navId: `band-${s.id}`, slotId: s.id})),
        ],
        setActiveSection,
    );

    /*
     * Put a roster member on the stage. The occupant itself is built by
     * `memberStageOccupant` — see it for why live, clip and nothing are three
     * different shapes rather than one with holes.
     */
    const watchMember = React.useCallback((m: typeof featured) => {
        const occupant = memberStageOccupant(m, highlights);
        if (occupant) setStageOccupant(occupant);
    }, [highlights, setStageOccupant]);

    const trailerThumb = youTubeThumb(drop?.trailerUrl);
    /*
     * `trailerThumb`, not `trailerUrl`.
     *
     * The trailer card IS its still — there is no other artwork on it — and
     * `youTubeThumb` only resolves one for a YouTube link. A trailer hosted
     * anywhere else passed this test, held the 400px column, and drew a card
     * with no image: a dark box on a dark page, which reads as the layout
     * being broken rather than as the column being empty. Meanwhile the stage
     * gave up the width for it.
     *
     * The column is now held only when something will actually be drawn in it.
     */
    /*
     * A slot exists if there is ANYTHING to feature. This tested `trailerThumb`
     * alone, which derives from `trailerUrl` — so a Drop that authored three
     * videos but left the trailer field empty created no featured slot at all,
     * and the whole row vanished rather than showing the videos it had.
     */
    const hasFeatured = Boolean(featured || trailerThumb || (drop?.videos as Array<{ url?: string }> | undefined)?.some(v => v.url));

    return (
        <ThemeProvider
            themeColors={drop.themeColors}
            typography={drop.typography}
            themeConfig={drop.themeConfig}
            tenantId={tenantId}
        >
            <SEO
                title={drop.seo?.title || `${drop.name} | DevourPlay`}
                description={drop.seo?.description || drop.description || `${drop.name} gaming hub`}
                url={`https://devourplay.gg/drop-in/${drop.slug}`}
                imageUrl={drop.seo?.socialShareImageUrl || drop.heroSection?.heroImageUrl}
            />
            {isPublicHub && <DevourTopNav text={drop.name} />}
            <div
                className={povPreset(drop.pov).rootClass
                    ? `drop-page-layout ${povPreset(drop.pov).rootClass}`
                    : "drop-page-layout"}
                /*
                 * `--drop-accent` is the name the EXISTING page widgets read;
                 * `brandStyle` publishes the token contract the spine reads.
                 * Both, because a hub still mounts page widgets through
                 * `renderSectionContent` for section types the spine has no
                 * rail for, and dropping the old name would drain the brand out
                 * of exactly those.
                 */
                style={{
                    /* Preset vars first — derived and authored tokens both beat them. */
                    ...povPreset(drop.pov).cssVars ?? {},
                    ...drop.themeColors?.primary
                        ? {"--drop-accent": drop.themeColors.primary} as React.CSSProperties
                        : {},
                    ...brandStyle(drop.themeColors) ?? {},
                    /*
                     * The stored spine-token record wins over the derived
                     * accent set: `brandStyle` DERIVES a few tokens from
                     * `themeColors`, while `spineTokens` is the brand's own
                     * authored record — spread last so an authored value
                     * always beats a derived one, and a field the record
                     * doesn't set keeps the derivation.
                     */
                    ...spineTokenStyle(drop.themeConfig?.spineTokens) ?? {},
                }}
            >
                <SurfaceStructure
                    style={style}
                    stage={structureStage}
                    pinnedSlotId={pinnedSlot}
                    /*
                     * NO COLUMN SWITCHER. The anchor never asks the viewer to
                     * choose what the third column holds: when an activation
                     * occupies it, the displaced cards — featured, roster,
                     * streaming — fall into the row below alongside the streak
                     * card. A BOARD | FEATURED toggle turns a layout decision
                     * into a question, and the answer is already known.
                     */
                    slots={[
                        ...dropSlots(enabledSections, {
                            nav: true,
                            featured: hasFeatured,
                            modes: Boolean(drop?.modes?.length),
                        }),
                        ...liveBlockSlots,
                    ]}
                    chrome={
                        <DropHubTopBar
                            drop={drop as SpineDrop}
                            memberName={memberName}
                            user={user}
                            pointDisplay={pointDisplay}
                            memberStanding={memberStanding}
                            badgeCount={myBadges.count}
                            campaignWeekLabel={campaignWeekLabel}
                            onSignOut={() => void devourLogout()}
                            onSignIn={handleLoginRequired}
                            onHelp={() => dispatch(toggleChatOpen(true))}
                            activeSection={activeSection}
                            setActiveSection={setActiveSection}
                            onInvite={handleInviteClick}
                        />
                    }
                    actionBar={
                        <SurfaceActionBar
                            iconUrl={drop.creatorProfileImageUrl}
                            iconAlt={drop.name}
                            /* The bar IS the page's navigation — see buildBarActions. */
                            actions={buildBarActions({
                                railSections, sectionNavId, isWheelSection, wheelAccessible,
                                requestWheel, setActiveSection, liveBlockSlots, contentBlockById,
                                purchaseUrl: drop.purchaseUrl, purchaseCta: copy.purchaseCta,
                                activeSection,
                            })}
                            /* What the viewer HAS — see the hook; empty renders nothing. */
                            standing={barStanding}
                            platformLogoUrl="/images/DevourPlayLogoWhite.png"
                            legal={SURFACE_LEGAL_LINKS}
                        />
                    }
                    /* One wrapper, so no future slot branch has to remember to report itself. */
                    renderSlot={withSectionTracking(makeRenderDropHubSlot({
                        drop, roster, members, sectionSublabels, sectionById, contentBlockById, liveBlockSlots,
                        isWheelSection, wheelAccessible, requestWheel, activeSection, setActiveSection,
                        resolvedStage, stageOccupant, setStageOccupant, stageIdleOccupant, stageBase,
                        sectionCtas, stageFaces, rosterCards, featured, onTitle, filterGameNames,
                        filterMode, highlights, copy, trailerThumb, watchMember, myBadges, gameNews,
                        renderSectionContent,
                    }))}
                />

                {/*
                  * The daily spin, mounted OUTSIDE the structure because it has
                  * no placement — `UNPLACED_GAMIFICATION_TYPES` lists WHEEL_SPIN
                  * for exactly this reason: a spin is a notification, not a card,
                  * so it appears when there is one to take and goes when it has
                  * been used. Until now nothing rendered it at all, so a surface
                  * whose nav offered a daily spin had no way to take one.
                  */}
                {surfaceWheel &&
                    <SurfaceWheel
                        modelId={surfaceWheel.modelId}
                        spinWheelConfigId={surfaceWheel.spinWheelConfigId}
                        spinsRemaining={surfaceWheel.spinsRemaining}
                        openRequest={wheelOpenRequest}
                    />}
            </div>
        </ThemeProvider>
    );
};

export default DropHubSurface;
